import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/PageHeader';
import { EmptyState } from '@/components/EmptyState';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { KeyRound, Plus, Loader2, Copy, Trash2, Check } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate } from '@/lib/utils';

export default function ApiKeys() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['api-keys'], queryFn: () => api.get('/api-keys').then(r => r.data) });
  const [open, setOpen] = React.useState(false);
  const [label, setLabel] = React.useState('');
  const [showKey, setShowKey] = React.useState(null);

  const createMut = useMutation({
    mutationFn: () => api.post('/api-keys', { label }).then(r => r.data),
    onSuccess: (d) => { setShowKey(d); setLabel(''); qc.invalidateQueries({ queryKey: ['api-keys'] }); },
    onError: () => toast.error('Could not create key'),
  });
  const delMut = useMutation({
    mutationFn: (id) => api.delete(`/api-keys/${id}`),
    onSuccess: () => { toast.success('Key revoked'); qc.invalidateQueries({ queryKey: ['api-keys'] }); },
  });

  const [copied, setCopied] = React.useState(false);
  const copy = () => {
    if (!showKey?.key) return;
    navigator.clipboard.writeText(showKey.key);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="space-y-6" data-testid="api-keys-page">
      <PageHeader
        eyebrow="Developers"
        title="API keys"
        description="Use REST APIs to submit tasks and manage agents programmatically."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="api-keys-new-button"><Plus className="mr-2 h-4 w-4" /> Create key</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create API key</DialogTitle>
                <DialogDescription>Give the key a descriptive label. The full secret is shown only once.</DialogDescription>
              </DialogHeader>
              <div className="space-y-2">
                <Label>Label</Label>
                <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Prod worker" data-testid="api-keys-label-input" />
              </div>
              <DialogFooter>
                <Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={() => createMut.mutate()} disabled={!label || createMut.isPending} data-testid="api-keys-confirm-create-button">
                  {createMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Create
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      {showKey && (
        <div className="rounded-lg border border-[hsl(var(--primary))/0.4] bg-[hsl(var(--primary))/0.08] p-5" data-testid="api-key-reveal">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-sm font-medium">Copy your new API key now</div>
              <p className="mt-1 text-xs text-[hsl(var(--text-3))]">This is the only time the full key is shown. Store it safely.</p>
              <pre className="mt-3 overflow-auto rounded-md border border-white/10 bg-black/50 p-3 font-mono text-xs text-[hsl(var(--primary))]">{showKey.key}</pre>
            </div>
            <div className="flex gap-2">
              <Button variant="secondary" size="sm" onClick={copy} data-testid="copy-api-key-button">
                {copied ? <Check className="mr-2 h-3.5 w-3.5" /> : <Copy className="mr-2 h-3.5 w-3.5" />}
                {copied ? 'Copied' : 'Copy'}
              </Button>
              <Button size="sm" onClick={() => setShowKey(null)}>Dismiss</Button>
            </div>
          </div>
        </div>
      )}

      <div className="rounded-lg border border-white/5 bg-[hsl(var(--surface-1))]">
        {isLoading ? (
          <div className="p-8 text-center text-sm text-[hsl(var(--text-3))]" data-testid="loading-skeleton">Loading…</div>
        ) : !data?.items?.length ? (
          <EmptyState icon={KeyRound} title="No API keys yet" description="Create one to authenticate external integrations." />
        ) : (
          <Table data-testid="api-keys-table">
            <TableHeader>
              <TableRow>
                <TableHead>Label</TableHead>
                <TableHead>Prefix</TableHead>
                <TableHead>Last used</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="w-[60px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((k) => (
                <TableRow key={k.id} data-testid={`api-keys-row-${k.id}`}>
                  <TableCell className="font-medium">{k.label}</TableCell>
                  <TableCell><span className="font-mono text-xs text-[hsl(var(--text-2))]">{k.prefix}…</span></TableCell>
                  <TableCell><span className="text-xs text-[hsl(var(--text-3))]">{k.last_used_at ? formatDate(k.last_used_at) : 'Never'}</span></TableCell>
                  <TableCell><span className="text-xs text-[hsl(var(--text-3))]">{formatDate(k.created_at)}</span></TableCell>
                  <TableCell>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button className="rounded p-1.5 text-[hsl(var(--text-3))] hover:bg-[hsl(var(--surface-2))] hover:text-[hsl(var(--danger))] transition-colors" aria-label="Revoke" data-testid={`api-keys-revoke-${k.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Revoke this key?</AlertDialogTitle>
                          <AlertDialogDescription>Any service using this key will fail to authenticate immediately.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => delMut.mutate(k.id)}>Revoke</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
